/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { Button, Form, message, Modal, Tabs } from "antd";
import copy from "copy-to-clipboard";
import moment from "moment";
import React, { useCallback, useState } from "react";
import { IdAndCreateTime } from "src/containers/license/LicenseList";
import { useFormatMessage } from "src/hooks";
import messageIds from "src/locales/messageIds";
import { LicenseListDetail } from "src/models/license";

const detailCss = css`
  display: flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.85);
  background-color: rgb(247, 249, 252);
  padding: 5px 13px;
  min-height: 32px;
  max-width: 670px;
  word-wrap: break-word;
  word-break: break-word;
`;

export default React.memo(
  (props: {
    isView: boolean;
    detail: LicenseListDetail;
    idandcreateTime: IdAndCreateTime[];
    onCancel: () => void;
  }) => {
    const { isView, detail, idandcreateTime, onCancel } = props;
    const formatMessage = useFormatMessage();
    const [activeKey, setActiveKey] = useState("1");

    const onCopy = useCallback(
      (license) => {
        copy(license);
        message.success(formatMessage(messageIds.common.copySuccessfully));
      },
      [formatMessage]
    );

    const onModalCancel = useCallback(() => {
      setActiveKey("1");
      onCancel();
    }, [onCancel]);

    return (
      <div>
        <Modal
          visible={isView}
          footer={""}
          onCancel={onModalCancel}
          css={css`
            width: 700px !important;
            top: 20px;
            .ant-modal-header {
              border-bottom: none;
            }
          `}
        >
          <Tabs
            type="card"
            activeKey={activeKey}
            tabBarStyle={{
              height: "30px",
              lineHeight: "30px",
            }}
            tabBarGutter={0}
            onChange={(activekey) => {
              setActiveKey(activekey);
            }}
            css={css`
              .ant-tabs-tab {
                background-color: #ffffff !important;
                border-radius: 4px 0px 0px 0px;
                width: 130px;
                display: flex;
                justify-content: center;
                align-items: center;
                border: 1px solid #ffffff;
              }
              .ant-tabs-tab.ant-tabs-tab-active {
                background-color: #84adff !important;
              }
              .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
                color: white;
              }
            `}
          >
            <Tabs.TabPane key="1" tab={formatMessage(messageIds.common.detail)}>
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: 20px;
                `}
              >
                <h1
                  css={css`
                    position: relative;
                    margin: 0px;
                    padding-left: 10px;
                    line-height: 16px;
                    font-size: 16px;
                    font-weight: 600;
                    color: rgb(61, 73, 102);
                    ::before {
                      content: "";
                      position: absolute;
                      left: 0px;
                      width: 3px;
                      height: 16px;
                      background: rgb(51, 119, 255);
                    }
                  `}
                >
                  {formatMessage(messageIds.common.detail)}
                </h1>
              </div>
              <Form
                layout="vertical"
                css={css`
                  max-height: 80vh;
                  overflow-y: auto;
                  overflow-x: hidden;
                `}
              >
                <Form.Item
                  label={formatMessage(messageIds.license.common.licenseType)}
                >
                  <div css={detailCss}>
                    {detail?.licenseType === "console"
                      ? formatMessage(messageIds.menu.license.console.name)
                      : detail?.licenseType === "robot-premium"
                      ? formatMessage(messageIds.menu.license.premiumRobot.name)
                      : detail?.licenseType === "ocr-local"
                      ? formatMessage(messageIds.menu.license.localOCR.name)
                      : formatMessage(messageIds.menu.license.studio.name)}
                  </div>
                </Form.Item>
                <Form.Item
                  label={formatMessage(messageIds.license.common.contractId)}
                >
                  <div css={detailCss}>
                    {detail?.contractId === "null"
                      ? formatMessage(messageIds.common.empty)
                      : detail?.contractId}
                  </div>
                </Form.Item>
                <Form.Item
                  label={formatMessage(messageIds.license.common.applySource)}
                >
                  <div css={detailCss}>
                    {idandcreateTime.filter((Item) => Item.id === detail?.id)?.[0]?.applySource === 1
                      ? formatMessage(messageIds.license.common.platformToCreate)
                      : idandcreateTime.filter((Item) => Item.id === detail?.id)?.[0]?.applySource === 2
                      ? formatMessage(messageIds.license.common.dingTalkApproval)
                      : idandcreateTime.filter((Item) => Item.id === detail?.id)?.[0]?.applySource === 3
                      ? formatMessage(messageIds.license.common.purchaseOnline)
                      :""}
                  </div>
                </Form.Item>
                <Form.Item
                  label={formatMessage(
                    messageIds.license.common.licensedToCompanyName
                  )}
                >
                  <div css={detailCss}>{detail?.licensedToCompanyName}</div>
                </Form.Item>
                <Form.Item
                  label={formatMessage(
                    messageIds.license.common.purposeOfApplication
                  )}
                >
                  <div css={detailCss}>
                    {detail?.application === 1
                      ? "POC"
                      : detail?.application === 2
                      ? formatMessage(messageIds.license.common.trial)
                      : detail?.application === 3
                      ? formatMessage(messageIds.license.common.formal)
                      : detail?.application === 4
                      ? formatMessage(messageIds.license.common.test)
                      : ""}
                  </div>
                </Form.Item>
                {detail?.application === 3 ? (
                  <Form.Item
                    label={formatMessage(messageIds.license.common.contractId)}
                  >
                    <div css={detailCss}>{detail?.contractId}</div>
                  </Form.Item>
                ) : (
                  ""
                )}
                <Form.Item
                  label={formatMessage(messageIds.license.common.licensedTo)}
                >
                  <div css={detailCss}>{detail?.licensedTo}</div>
                </Form.Item>
                <Form.Item
                  label={formatMessage(messageIds.license.common.machineKey)}
                >
                  <div css={detailCss}>{detail?.machineKey}</div>
                </Form.Item>
                <Form.Item
                  label={formatMessage(messageIds.license.common.fromTo)}
                >
                  <div css={detailCss}>{`${moment(detail?.notBefore).format(
                    "YYYY-MM-DD HH:mm:ss"
                  )} ~ ${moment(detail?.notAfter).format(
                    "YYYY-MM-DD HH:mm:ss"
                  )}`}</div>
                </Form.Item>

                {(detail?.licenseType === "studio" ||
                  detail?.licenseType === "robot-premium") &&
                  detail?.licenseProducts?.map((item) =>
                    item.name === "studio" || item.name === "robot" ? (
                      ""
                    ) : (
                      <Form.Item
                        key={`${item.name}_${item.sku}`}
                        label={
                          item.name === "local_ai" &&
                          formatMessage(messageIds.license.common.localOcr)
                        }
                      >
                        <div css={detailCss}>{item.amount}</div>
                      </Form.Item>
                    )
                  )}

                {detail?.licenseType === "console"
                  ? detail?.licenseProducts?.map((item) =>
                      item.name === "console" || item.name === "app" ? (
                        ""
                      ) : (
                        <Form.Item
                          key={`${item.name}_${item.sku}`}
                          label={
                            item.name === "studio"
                              ? item.sku === "enterprise"
                                ? formatMessage(
                                    messageIds.license.console.numStudio
                                  )
                                : formatMessage(
                                    messageIds.license.console.numFloatingStudio
                                  )
                              : item.name === "robot"
                              ? item.sku === "enterprise_basic"
                                ? formatMessage(
                                    messageIds.license.console.numBasicRobot
                                  )
                                : item.sku === "enterprise_pro"
                                ? formatMessage(
                                    messageIds.license.console.numPremiumRobot
                                  )
                                : formatMessage(
                                    messageIds.license.console.numFloatingRobot
                                  )
                              : item.name === "documentunderstanding"
                              ? item.sku === "pdfimage"
                                ? formatMessage(
                                    messageIds.license.console.numPdfImage
                                  )
                                : formatMessage(
                                    messageIds.license.console.numPdfText
                                  )
                              : item.name === "vicode"
                              ? formatMessage(
                                  messageIds.license.console.numVicode
                                )
                              : ""
                          }
                        >
                          <div css={detailCss}>{item.amount}</div>
                        </Form.Item>
                      )
                    )
                  : ""}
                <Form.Item
                  label={formatMessage(messageIds.license.common.requestedBy)}
                >
                  <div css={detailCss}>{detail?.requestedBy}</div>
                </Form.Item>
                <Form.Item
                  label={formatMessage(messageIds.license.common.createBy)}
                >
                  <div css={detailCss}>{detail?.createBy}</div>
                </Form.Item>
                <Form.Item
                  label={formatMessage(messageIds.license.common.issuanceTime)}
                >
                  <div css={detailCss}>
                    {moment(
                      idandcreateTime.filter(
                        (Item) => Item.id === detail?.id
                      )?.[0]?.createTime
                    ).format("YYYY-MM-DD HH:mm:ss")}
                  </div>
                </Form.Item>
                <Form.Item label={formatMessage(messageIds.common.description)}>
                  <div
                    css={css`
                      display: flex;
                      color: rgba(0, 0, 0, 0.85);
                      background-color: rgb(247, 249, 252);
                      padding: 5px 13px;
                      min-height: 80px;
                      max-width: 670px;
                      word-wrap: break-word;
                      word-break: break-word;
                    `}
                  >
                    {detail?.note === "null" ? "" : detail?.note}
                  </div>
                </Form.Item>
              </Form>
            </Tabs.TabPane>
            <Tabs.TabPane
              key="2"
              tab={formatMessage(messageIds.license.common.licenseInformation)}
            >
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: 20px;
                `}
              >
                <h1
                  css={css`
                    position: relative;
                    margin: 0px;
                    padding-left: 10px;
                    line-height: 16px;
                    font-size: 16px;
                    font-weight: 600;
                    color: rgb(61, 73, 102);
                    ::before {
                      content: "";
                      position: absolute;
                      left: 0px;
                      width: 3px;
                      height: 16px;
                      background: rgb(51, 119, 255);
                    }
                  `}
                >
                  {formatMessage(messageIds.license.common.licenseInformation)}
                </h1>
              </div>
              <Form
                layout="vertical"
                css={css`
                  max-height: 80vh;
                  overflow-y: auto;
                  overflow-x: hidden;
                `}
              >
                <Form.Item
                  label={formatMessage(messageIds.license.common.name)}
                >
                  <div
                    css={css`
                      display: flex;
                      color: rgba(0, 0, 0, 0.85);
                      background-color: rgb(247, 249, 252);
                      padding: 5px 13px;
                      min-height: 80px;
                      max-width: 670px;
                      word-wrap: break-word;
                      word-break: break-word;
                    `}
                  >
                    {detail?.licenseStrs}
                  </div>
                </Form.Item>
              </Form>
            </Tabs.TabPane>
          </Tabs>

          <div
            css={css`
              margin-top: 20px;
              text-align: right;
            `}
          >
            {activeKey === "2" ? (
              <Button
                type="primary"
                css={css`
                  height: 32px;
                  min-width: 88px;
                  border-radius: 4px;
                  margin-right: 10px;
                `}
                onClick={() => onCopy(detail?.licenseStrs)}
              >
                {formatMessage(messageIds.license.common.copyLicense)}
              </Button>
            ) : (
              ""
            )}
            <Button
              type="primary"
              css={css`
                height: 32px;
                min-width: 88px;
                border-radius: 4px;
              `}
              onClick={onModalCancel}
            >
              {formatMessage(messageIds.common.confirm)}
            </Button>
          </div>
        </Modal>
      </div>
    );
  }
);
